/**
 * KairosIQ Market Intelligence Dashboard
 * 
 * AI-Forward analytics dashboard for Arizona real estate market intelligence
 * Features 1.3M+ property records with real-time market analysis
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/free-solid-svg-icons';

// Import types
import type { 
  MarketIntelligenceData,
  ZipCodeOption,
  LoadingState,
  ErrorState
} from '../../../types/marketIntelligence.types';

// Import components
import ZipCodeSelector from './components/ZipCodeSelector';
import MarketTemperatureCard from './components/MarketTemperatureCard';
import CoreMetricsGrid from './components/CoreMetricsGrid';
import MarketTrendsChart from './components/MarketTrendsChart';
import TopPerformersTable from './components/TopPerformersTable';
import AIInsightsPanel from './components/AIInsightsPanel';

// Arizona ZIP codes - sample data (will be moved to a service later)
const ARIZONA_ZIP_CODES: ZipCodeOption[] = [
  { value: '85001', label: '85001 - Phoenix Downtown', city: 'Phoenix' },
  { value: '85331', label: '85331 - Cave Creek', city: 'Cave Creek' },
  { value: '85375', label: '85375 - Surprise', city: 'Surprise' },
  { value: '85138', label: '85138 - Peoria', city: 'Peoria' },
  { value: '85262', label: '85262 - Scottsdale', city: 'Scottsdale' },
  { value: '85255', label: '85255 - Paradise Valley', city: 'Paradise Valley' },
  // Add more ZIP codes as needed...
];

const MarketIntelligenceDashboard: React.FC = () => {
  // State management
  const [selectedZip, setSelectedZip] = useState<string>('85001');
  const [data, setData] = useState<MarketIntelligenceData | null>(null);
  const [loading, setLoading] = useState<LoadingState>({
    dashboard: false,
    zipSelector: false,
    temperatureCard: false,
    metricsGrid: false,
    trendsChart: false,
    performersTable: false,
    insightsPanel: false
  });
  const [error, setError] = useState<ErrorState | null>(null);

  // Fetch real market data from API
  const fetchMarketData = async (zipCode: string) => {
    setLoading(prev => ({ ...prev, dashboard: true }));
    setError(null);

    try {
      // Make actual API call to fetch real data from your database
      const response = await fetch(`http://localhost:8080/api/market-intelligence/zip/${zipCode}/latest`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No market data available for ZIP code ${zipCode}`);
        }
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'API returned error');
      }

      // The API response has the data nested under 'data' property
      const apiData = result.data;
      
      // Transform API response to match frontend interface
      const transformedData: MarketIntelligenceData = {
        zip_code: apiData.zip_code,
        analysis_date: apiData.analysis_date,
        market_summary: {
          daily_sales_count: apiData.market_summary.daily_sales_count,
          median_close_price: apiData.market_summary.median_close_price,
          total_volume: apiData.market_summary.total_volume,
          market_temperature: apiData.market_summary.market_temperature,
          market_action_index: apiData.market_summary.market_action_index
        },
        market_metrics: {
          median_dom: apiData.market_metrics.median_dom,
          median_ppsf: apiData.market_metrics.median_ppsf,
          active_listings_count: apiData.market_metrics.active_listings_count,
          quick_sales_under_30: apiData.market_metrics.quick_sales_under_30,
          avg_price_change_pct: apiData.market_metrics.avg_price_change_pct
        },
        property_analytics: {
          average_bedrooms: apiData.property_analytics.average_bedrooms,
          average_bathrooms: apiData.property_analytics.average_bathrooms,
          properties_sold_below_list: apiData.property_analytics.properties_sold_below_list,
          properties_sold_above_list: apiData.property_analytics.properties_sold_above_list
        },
        ai_insights: {
          data_quality_score: apiData.ai_insights.data_quality_score,
          confidence_level: apiData.ai_insights.confidence_level,
          market_emoji: apiData.ai_insights.market_emoji,
          last_updated: apiData.ai_insights.last_updated,
          total_records_analyzed: apiData.ai_insights.total_records_analyzed || 0,
          time_period: apiData.ai_insights.time_period || 'Unknown',
          data_source: apiData.ai_insights.data_source || 'ARMLS Data',
          data_strategy: apiData.ai_insights.data_strategy || 'Recent data',
          data_freshness: apiData.ai_insights.data_freshness || 'Unknown',
          data_reliability: apiData.ai_insights.data_reliability || 'Limited',
          market_insight: apiData.ai_insights.market_insight || 'Limited data available'
        }
      };

      setData(transformedData);
    } catch (err) {
      console.error('Market data fetch error:', err);
      setError({
        type: 'network',
        message: 'Failed to fetch market data',
        details: err instanceof Error ? err.message : 'Unknown error',
        retryable: true
      });
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  };

  // Initial data load
  useEffect(() => {
    fetchMarketData(selectedZip);
  }, [selectedZip]);

  // Handle ZIP code change
  const handleZipCodeChange = (zipCode: string) => {
    setSelectedZip(zipCode);
    // Data will be fetched by useEffect
  };

  // Handle retry
  const handleRetry = () => {
    fetchMarketData(selectedZip);
  };

  return (
    <Container fluid className="market-intelligence-dashboard p-4">
      {/* AI Intelligence Header */}
      <div 
        className="intelligence-header mb-4"
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          backdropFilter: 'blur(15px)',
          margin: '0px 0px 2rem',
          position: 'relative',
          overflow: 'hidden',
          padding: '1rem',
          borderRadius: '0px 0px 16px 16px'
        }}
      >
        <div className="market-intelligence-header-layout">
          <div className="market-intelligence-content">
            <div style={{ position: 'relative', marginRight: '16px' }}>
              <FontAwesomeIcon 
                icon={faBolt} 
                style={{ 
                  color: '#00d4ff', 
                  fontSize: '32px',
                  filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.5))' 
                }} 
              />
            </div>
            <div>
              <h4 className="market-intelligence-title" style={{ 
                color: 'rgb(31, 41, 55)',
                textShadow: 'rgba(0, 212, 255, 0.3) 0px 0px 20px',
                fontSize: '1.7rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, rgb(31, 41, 55) 0%, rgb(55, 65, 81) 100%) text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '4px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                AI Market Intelligence Center
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                Real-time analytics powered by 1.3M+ property records
              </p>
            </div>
          </div>
          
          {/* AI Status Indicator */}
          <div 
            className="ai-status-indicator"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              padding: '8px 16px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)'
            }}
          >
            <div 
              className="rounded-circle me-2"
              style={{
                width: '8px',
                height: '8px',
                background: '#10b981',
                animation: 'pulse 2s infinite',
                display: 'inline-block'
              }}
            />
            <span 
              className="fw-semibold text-success" 
              style={{ 
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              AI ACTIVE
            </span>
          </div>
        </div>
      </div>

      {/* Data Quality Banner */}
      {data?.ai_insights && (
        <Alert 
          variant={data.ai_insights.data_strategy?.includes('Fallback') ? 'warning' : 'info'}
          className="mb-4"
          style={{
            background: data.ai_insights.data_strategy?.includes('Fallback') 
              ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
            border: `1px solid ${data.ai_insights.data_strategy?.includes('Fallback') ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
            borderRadius: '12px'
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <div className="fw-semibold mb-1">
                📊 Data Quality: {data.ai_insights.data_reliability} • 
                {data.ai_insights.data_freshness} • 
                {data.ai_insights.data_strategy}
              </div>
              <div className="small text-muted">
                Period: {data.ai_insights.time_period} • 
                Records: {data.ai_insights.total_records_analyzed.toLocaleString()} • 
                Source: {data.ai_insights.data_source}
                {data.ai_insights.data_strategy?.includes('Fallback') && (
                  <span className="text-warning ms-2">
                    ⚠️ Using extended historical data due to limited recent activity
                  </span>
                )}
              </div>
            </div>
            <div 
              className="badge"
              style={{
                background: data.ai_insights.data_reliability === 'High' ? '#10b981' :
                           data.ai_insights.data_reliability === 'Good' ? '#3b82f6' :
                           data.ai_insights.data_reliability === 'Moderate' ? '#f59e0b' : '#ef4444',
                color: 'white',
                fontSize: '11px',
                padding: '6px 12px',
                borderRadius: '20px'
              }}
            >
              {data.ai_insights.data_reliability} Reliability
            </div>
          </div>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Error:</strong> {error.message}
              {error.details && (
                <div className="small text-muted mt-1">{error.details}</div>
              )}
            </div>
            {error.retryable && (
              <button 
                className="btn btn-outline-danger btn-sm"
                onClick={handleRetry}
                disabled={loading.dashboard}
              >
                Retry
              </button>
            )}
          </div>
        </Alert>
      )}

      {/* ZIP Code Selector + Market Temperature */}
      <Row className="mb-4">
        <Col md={8}>
          <ZipCodeSelector
            value={selectedZip}
            onChange={handleZipCodeChange}
            arizonaZipCodes={ARIZONA_ZIP_CODES}
            disabled={loading.dashboard}
            showDataAvailability={true}
          />
        </Col>
        <Col md={4}>
          <MarketTemperatureCard
            temperature={data?.market_summary.market_temperature}
            mai={data?.market_summary.market_action_index}
            emoji={data?.ai_insights.market_emoji}
            loading={loading.dashboard}
          />
        </Col>
      </Row>

      {/* Core Metrics Grid (4 columns) */}
      <Row className="mb-4">
        <Col>
          <CoreMetricsGrid
            medianPrice={data?.market_summary.median_close_price}
            totalVolume={data?.market_summary.total_volume}
            medianDOM={data?.market_metrics.median_dom}
            activeListing={data?.market_metrics.active_listings_count}
            pricePerSqft={data?.market_metrics.median_ppsf}
            salesCount={data?.market_summary.daily_sales_count}
            loading={loading.dashboard}
          />
        </Col>
      </Row>

      {/* Charts and Analysis */}
      <Row className="mb-4">
        <Col md={8}>
          <MarketTrendsChart
            zipCode={selectedZip}
            period="90d"
            height={300}
            showTooltips={true}
          />
        </Col>
        <Col md={4}>
          <AIInsightsPanel
            dataQuality={data?.ai_insights.data_quality_score}
            confidence={data?.ai_insights.confidence_level}
            lastUpdated={data?.ai_insights.last_updated}
            recordCount={19343}
            loading={loading.dashboard}
          />
        </Col>
      </Row>

      {/* Top Performers Table */}
      <Row>
        <Col>
          <TopPerformersTable
            metric="mai"
            limit={20}
            order="desc"
            onZipCodeClick={handleZipCodeChange}
            showPagination={true}
            showExport={true}
          />
        </Col>
      </Row>

      {/* Custom CSS for animations and responsive styles */}
      <style>{`
        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 1;
          }
        }
        
        /* Default layout styles */
        .market-intelligence-header-layout {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .market-intelligence-content {
          display: flex;
          align-items: center;
        }
        
        .ai-status-indicator {
          display: flex;
          align-items: center;
        }
        
        @media (max-width: 500px) {
          .market-intelligence-title {
            font-size: 1.2rem !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            max-width: 250px !important;
            text-align: center !important;
          }
          
          .market-intelligence-header-layout {
            flex-direction: column;
            align-items: center;
          }
          
          .market-intelligence-content {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          
          .market-intelligence-content > div:first-child {
            margin-right: 0 !important;
            margin-bottom: 12px;
          }
          
          .ai-status-indicator {
            margin-top: 15px;
            align-self: center;
          }
        }
      `}</style>
    </Container>
  );
};

export default MarketIntelligenceDashboard;